import ResizableTable from "../../../Components/ResizableTable/ResizableTable";
import { PageHeader } from "@ant-design/pro-components";
import { CampaignOutlined } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Popconfirm,
  Spin,
  Tag,
  message,
} from "antd";
import { memo, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import CheckPermission from "../../../Util/CheckPermission";
import ConvertNumbersToEn from "../../../Util/ConvertNumbersToEn";
import {
  SmsCampaignStatus,
  SmsQueueStatus,
  isSmsCampaignEditable,
  campaignStatusColor,
  campaignStatusLabel,
  formatCampaignSendAt,
  formatJalaliDateTime,
  queueStatusColor,
  queueStatusLabel,
} from "../../../Util/smsCampaign";
import styles from "./SmsCampaignView.module.css";

const SmsCampaignView = ({ canEdit, canCancel, canList }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [queues, setQueues] = useState([]);
  const [mobileSearch, setMobileSearch] = useState("");
  const [ready, setReady] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState("");

  const editable =
    isSmsCampaignEditable(campaign) && canEdit;
  const cancellable =
    canCancel &&
    (campaign?.status === SmsCampaignStatus.Pending ||
      campaign?.status === SmsCampaignStatus.Sending);
  const restorable =
    canCancel && campaign?.status === SmsCampaignStatus.Cancelled;

  const loadDetails = useCallback(async ({ silent } = {}) => {
    if (!silent) setReady(false);
    setError("");
    try {
      const res = await api.get("/smscampaign/details/" + id);
      const data = res.data?.data;
      if (!data) {
        setError("کمپین پیدا نشد.");
        return;
      }
      setCampaign(data);
      setQueues(Array.isArray(data.queues) ? data.queues : []);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error?.message || "خطا در دریافت کمپین"
      );
    } finally {
      setReady(true);
    }
  }, [id]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const cancelCampaign = async () => {
    setCancelling(true);
    setError("");
    try {
      await api.post("/smscampaign/cancel/" + id);
      message.success("کمپین لغو شد.");
      await loadDetails({ silent: true });
    } catch (requestError) {
      const text =
        requestError.response?.data?.error?.message || "لغو کمپین انجام نشد.";
      setError(text);
      message.error(text);
    } finally {
      setCancelling(false);
    }
  };

  const restoreCampaign = async () => {
    setRestoring(true);
    setError("");
    try {
      await api.post("/smscampaign/restore/" + id);
      message.success("کمپین از حالت لغو خارج شد.");
      await loadDetails({ silent: true });
    } catch (requestError) {
      const text =
        requestError.response?.data?.error?.message ||
        "خروج کمپین از حالت لغو انجام نشد.";
      setError(text);
      message.error(text);
    } finally {
      setRestoring(false);
    }
  };

  const visibleQueues = queues.filter(
    (queue) => !mobileSearch || String(queue.mobile).includes(mobileSearch)
  );

  const queueColumns = [
    {
      title: "موبایل",
      dataIndex: "mobile",
      render: (value) => <span dir="ltr">{value}</span>,
    },
    {
      title: "وضعیت",
      dataIndex: "status",
      filters: [
        SmsQueueStatus.Pending,
        SmsQueueStatus.Sending,
        SmsQueueStatus.Sent,
        SmsQueueStatus.Failed,
        SmsQueueStatus.Cancelled,
      ].map((status) => ({
        text: queueStatusLabel(status),
        value: status,
      })),
      onFilter: (value, record) => Number(record.status) === Number(value),
      render: (status) => (
        <Tag color={queueStatusColor(status)}>{queueStatusLabel(status)}</Tag>
      ),
    },
    {
      title: "زمان تنظیم شده ارسال",
      dataIndex: "send_at",
      render: (value) => <span dir="ltr">{formatCampaignSendAt(value)}</span>,
    },
    {
      title: "زمان ارسال شده",
      dataIndex: "try_sent_at",
      render: (value) => <span dir="ltr">{formatJalaliDateTime(value)}</span>,
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={campaign?.title || "کمپین پیامک"}
        tags={
          campaign ? (
            <Tag color={campaignStatusColor(campaign.status)}>
              {campaignStatusLabel(campaign.status)}
            </Tag>
          ) : null
        }
        avatar={{
          icon: <CampaignOutlined />,
          size: "large",
          style: { paddingTop: "8px" },
        }}
      />

      <Spin spinning={!ready || cancelling || restoring}>
        {error ? <Alert type="error" message={error} /> : null}

        {ready && campaign ? (
          <Card className={styles.details}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="عنوان">{campaign.title}</Descriptions.Item>
              <Descriptions.Item label="خط ارسالی">
                {campaign.line_number ? (
                  <span dir="ltr">{campaign.line_number}</span>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="زمان ارسال">
                <span dir="ltr">{formatCampaignSendAt(campaign.send_at)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="زمان ایجاد">
                <span dir="ltr">{formatJalaliDateTime(campaign.createdAt)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="هزینه (ریال)">
                {(campaign.cost?.total ?? 0).toLocaleString("fa-IR", { maximumFractionDigits: 20 })}
                {" "}({(campaign.cost?.missingCount ?? 0).toLocaleString("fa-IR")} پیامک با هزینه نامشخص)
              </Descriptions.Item>
              <Descriptions.Item label="متن پیام">
                <p className={styles.message}>{campaign.message}</p>
              </Descriptions.Item>
            </Descriptions>

            <div className={styles.actions}>
              {editable ? (
                <Button
                  type="primary"
                  onClick={() => navigate("/sms-campaigns/" + id + "/edit")}
                >
                  ویرایش
                </Button>
              ) : null}
              {cancellable ? (
                <Popconfirm
                  title="شماره‌های در انتظار لغو می‌شوند. ادامه می‌دهید؟"
                  okText="تایید"
                  cancelText="انصراف"
                  onConfirm={cancelCampaign}
                >
                  <Button danger loading={cancelling}>
                    لغو کمپین
                  </Button>
                </Popconfirm>
              ) : null}
              {restorable ? (
                <Popconfirm
                  title="گیرنده‌های لغوشده به صف ارسال برمی‌گردند. ادامه می‌دهید؟"
                  okText="تایید"
                  cancelText="انصراف"
                  onConfirm={restoreCampaign}
                >
                  <Button danger loading={restoring}>
                    خروج از حالت لغو
                  </Button>
                </Popconfirm>
              ) : null}
              <Button onClick={() => navigate(canList ? "/sms-campaigns" : "/")}>بازگشت</Button>
            </div>
          </Card>
        ) : null}

        {ready && campaign ? (
          <Card title="گیرنده‌ها">
            <div className={styles.stats}>
              <Tag>کل {campaign.counts?.total || 0}</Tag>
              <Tag color="gold">در انتظار {campaign.counts?.pending || 0}</Tag>
              <Tag color="blue">در حال ارسال {campaign.counts?.sending || 0}</Tag>
              <Tag color="green">ارسال‌شده {campaign.counts?.sent || 0}</Tag>
              <Tag color="red">ناموفق {campaign.counts?.failed || 0}</Tag>
              <Tag>لغوشده {campaign.counts?.cancelled || 0}</Tag>
            </div>
            <Input
              className={styles.search}
              allowClear
              placeholder="جستجوی شماره"
              value={mobileSearch}
              onChange={(event) =>
                setMobileSearch(ConvertNumbersToEn(event.target.value.trim()))
              }
            />
            <ResizableTable
              tableKey="sms-campaign-queues"
              rowKey="id"
              columns={queueColumns}
              dataSource={visibleQueues}
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
                showTotal: (total) => `${total} شماره`,
              }}
              locale={{ emptyText: "گیرنده‌ای ثبت نشده است." }}
            />
          </Card>
        ) : null}
      </Spin>
    </div>
  );
};

const mapStateToProps = (state) => {
  const permissions = state.userData ? state.userData.permissions : [];
  return {
    canEdit: CheckPermission("sms_campaign_edit", permissions),
    canCancel: CheckPermission("sms_campaign_cancel", permissions),
    canList: CheckPermission("sms_campaign_list", permissions),
  };
};

export default connect(mapStateToProps)(memo(SmsCampaignView));
